import { TagField, DateField, UrlField, NumberField } from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";
import { Typography, Tag, Space, Tooltip } from "antd";
import { CSSProperties, ReactNode } from "react";
import {
  MailOutlined,
  PhoneOutlined,
  LinkOutlined,
  CalendarOutlined,
  NumberOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { colors } from "config/theme";

const { Text } = Typography;

export type FieldType =
  | "text"
  | "email"
  | "phone"
  | "url"
  | "date"
  | "number"
  | "currency"
  | "percentage"
  | "tag"
  | "status"
  | "boolean"
  | "code"
  | "notes";

interface ShowFieldProps {
  label: string;
  value?: string | number | boolean | Date | null | undefined;
  type?: FieldType;
  icon?: ReactNode;
  tooltip?: string;
  dateFormat?: string;
  tagColor?: string;
  statusColors?: Record<string, string>;
  prefix?: string;
  currency?: string;
  suffix?: string;
  copyable?: boolean;
  monospace?: boolean;
  empty?: string;
}

const fieldStyles: Record<string, CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minHeight: 52,
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: colors.textTertiary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  valueContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minHeight: 24,
  },
  value: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  emptyValue: {
    fontSize: 14,
    color: colors.textDisabled,
    fontStyle: "italic",
  },
  icon: {
    fontSize: 14,
    color: colors.textTertiary,
  },
  codeValue: {
    fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
    fontSize: 13,
    backgroundColor: colors.bgMuted,
    padding: "4px 8px",
    borderRadius: 6,
    color: colors.textPrimary,
    wordBreak: "break-all",
  },
};

const defaultStatusColors: Record<string, string> = {
  active: "green",
  inactive: "default",
  "on hold": "orange",
  "not on hold": "green",
  pending: "blue",
  completed: "green",
  failed: "red",
  processing: "cyan",
  enabled: "green",
  "not enabled": "default",
  permitted: "green",
  "not permitted": "default",
  full: "green",
  trial: "blue",
  draft: "default",
  valid: "green",
  invalid: "red",
  fulfilled: "green",
};

const typeIcons: Partial<Record<FieldType, ReactNode>> = {
  email: <MailOutlined />,
  phone: <PhoneOutlined />,
  url: <LinkOutlined />,
  date: <CalendarOutlined />,
  number: <NumberOutlined />,
  notes: <FileTextOutlined />,
};

export const ShowField: React.FC<ShowFieldProps> = ({
  label,
  value,
  type = "text",
  icon,
  tooltip,
  dateFormat = "DD-MMM-YYYY",
  tagColor,
  statusColors = {},
  prefix,
  currency = "USD",
  suffix,
  copyable = false,
  monospace = false,
  empty = "—",
}) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  const renderIcon = () => {
    const displayIcon = icon || typeIcons[type];
    if (!displayIcon) return null;
    return <span style={fieldStyles.icon}>{displayIcon}</span>;
  };

  const renderValue = () => {
    if (!hasValue) {
      return <Text style={fieldStyles.emptyValue}>{empty}</Text>;
    }

    const mergedStatusColors = { ...defaultStatusColors, ...statusColors };
    const stringValue = String(value);

    switch (type) {
      case "email":
        return <CopyableEmailTag value={stringValue} />;

      case "url":
        return <UrlField value={stringValue} style={fieldStyles.value} />;

      case "date":
        return (
          <DateField
            value={value as string}
            format={dateFormat}
            style={fieldStyles.value}
          />
        );

      case "number":
        return (
          <NumberField
            value={value as number}
            style={fieldStyles.value}
            options={{ notation: "standard" }}
          />
        );

      case "currency":
        return (
          <NumberField
            value={value as number}
            style={fieldStyles.value}
            options={{ style: "currency", currency }}
          />
        );

      case "percentage":
        return (
          <NumberField
            value={(value as number) / 100}
            style={fieldStyles.value}
            options={{ style: "percent" }}
          />
        );

      case "tag":
        return (
          <Tag color={tagColor} style={{ margin: 0 }}>
            {stringValue}
          </Tag>
        );

      case "status":
      case "boolean":
        const color =
          tagColor ||
          mergedStatusColors[stringValue.toLowerCase()] ||
          "default";
        return (
          <Tag color={color} style={{ margin: 0 }}>
            {stringValue}
          </Tag>
        );

      case "code":
        return (
          <Text
            copyable={copyable}
            style={fieldStyles.codeValue}
          >
            {stringValue}
          </Text>
        );

      case "notes":
        return (
          <Text
            style={{
              ...fieldStyles.value,
              whiteSpace: "pre-wrap",
              color: colors.textSecondary,
            }}
          >
            {stringValue}
          </Text>
        );

      case "phone":
        return (
          <Text style={fieldStyles.value} copyable={copyable}>
            {stringValue}
          </Text>
        );

      default:
        return (
          <Text
            style={{
              ...fieldStyles.value,
              ...(monospace ? { fontFamily: "'SF Mono', monospace" } : {}),
            }}
            copyable={copyable}
          >
            {`${prefix || ""}${stringValue}${suffix || ""}`}
          </Text>
        );
    }
  };

  return (
    <div style={fieldStyles.container}>
      <div style={fieldStyles.labelRow}>
        <Text style={fieldStyles.label}>{label}</Text>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ fontSize: 12, color: colors.textDisabled }} />
          </Tooltip>
        )}
      </div>
      <div style={fieldStyles.valueContainer}>
        {renderIcon()}
        {renderValue()}
      </div>
    </div>
  );
};

export default ShowField;
